/**
 * BlackFridayPage - Entry point for the Black Friday promotions page
 *
 * Wraps content in QueryClientProvider + UiProvider + CheckoutProvider + BlackFridayProvider.
 * Composes: LoadingOverlay, Blocks (CMS banners), CategoryGrid, SubCategorySection.
 */

import { QueryClientProvider } from '@tanstack/react-query'

import { Blocks } from './Blocks'
import { CategoryGrid } from './CategoryGrid'
import { LoadingOverlay } from './LoadingOverlay'
import { SubCategorySection } from './SubCategorySection'
import { getQueryClient } from '../../../../Infrastructure'
import { CheckoutProvider } from '../../../Checkout'
import { UiProvider } from '../../../Theme'
import { SectionHeader, TextLink } from '../../../UiBlocks'
import { BlackFridayProvider, useBlackFriday } from '../../state'


function ErrorFallback({ error }: { error: Error }) {
  return (
    <div className="flex flex-col items-center justify-center p-8 text-center" role="alert">
      <SectionHeader as="h2" size="lg" className="mb-4 text-red-400 bg-transparent">
        Eroare la incarcarea promotiilor
      </SectionHeader>
      <p className="font-main-font text-base text-white/80">{error.message}</p>
      <button
        onClick={() => window.location.reload()}
        className="mt-6 rounded-lg bg-main-color px-6 py-3 font-main-font font-bold text-text-color transition-all hover:scale-105"
      >
        Reincarca pagina
      </button>
    </div>
  )
}

function BlackFridayContent() {
  const { loading, error } = useBlackFriday()

  if (error) return <ErrorFallback error={error} />

  return (
    <div className='mb-8'>
      {loading && <LoadingOverlay />}

      <div className="mb-8 mt-5 xl:mt-10">
        <TextLink
          href={`${window.BASE_URL}home`}
          className="react-css text-white hover:text-white/80 active:text-white focus:text-white"
        >
          Inapoi la pagina principala
        </TextLink>
      </div>

      <div>
        <div className="flex w-full flex-col items-center justify-center gap-6">
          <Blocks />
        </div>
        <CategoryGrid />
        <SubCategorySection />
      </div>
    </div>
  )
}

export function BlackFridayPage() {
  const queryClient = getQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      <UiProvider>
        <CheckoutProvider>
          <BlackFridayProvider>
            <BlackFridayContent />
          </BlackFridayProvider>
        </CheckoutProvider>
      </UiProvider>
    </QueryClientProvider>
  )
}
